var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import cl from "clsx";
import React, { forwardRef, useCallback, useRef, } from "react";
import { omit } from "../../../util/index.js";
import { useMergeRefs } from "../../../util/hooks/index.js";
import filteredOptionsUtil from "../FilteredOptions/filtered-options-util.js";
import { useFilteredOptionsContext } from "../FilteredOptions/filteredOptionsContext.js";
import { useSelectedOptionsContext } from "../SelectedOptions/selectedOptionsContext.js";
import { useInputContext } from "./Input.context.js";
const Input = forwardRef((_a, ref) => {
    var { inputClassName, shouldShowSelectedOptions, placeholder } = _a, rest = __rest(_a, ["inputClassName", "shouldShowSelectedOptions", "placeholder"]);
    const internalRef = useRef(null);
    const mergedRefs = useMergeRefs(ref, internalRef);
    const { clearInput, inputProps, onChange, size, value, searchTerm, setValue, hideCaret, setHideCaret, } = useInputContext();
    const { selectedOptions, removeSelectedOption, toggleOption, isMultiSelect, maxSelected, } = useSelectedOptionsContext();
    const { activeDecendantId, allowNewValues, currentOption, filteredOptions, isValueNew, toggleIsListOpen, isListOpen, ariaDescribedBy, setIsMouseLastUsedInputDevice, shouldAutocomplete, virtualFocus, } = useFilteredOptionsContext();
    const onEnter = useCallback((event) => {
        const isTextInSelectedOptions = (text) => selectedOptions.some((option) => option.label.toLocaleLowerCase() === text.toLocaleLowerCase());
        if (currentOption) {
            event.preventDefault();
            // Selecting a value from the dropdown / FilteredOptions
            toggleOption(currentOption, event);
            if (!isMultiSelect && !isTextInSelectedOptions(currentOption.label)) {
                toggleIsListOpen(false);
            }
        }
        else if (isTextInSelectedOptions(value)) {
            event.preventDefault();
            // Trying to set the same value that is already set, so just clearing the input
            clearInput(event);
        }
        else if ((allowNewValues || shouldAutocomplete) && value !== "") {
            event.preventDefault();
            // Autocompleting or adding a new value
            const selectedValue = allowNewValues && isValueNew
                ? { label: value, value }
                : filteredOptionsUtil.getFirstValueStartingWith(value, filteredOptions) || filteredOptions[0];
            if (!selectedValue) {
                return;
            }
            toggleOption(selectedValue, event);
            if (!isMultiSelect && !isTextInSelectedOptions(selectedValue.label)) {
                toggleIsListOpen(false);
            }
        }
    }, [
        allowNewValues,
        clearInput,
        currentOption,
        filteredOptions,
        isMultiSelect,
        isValueNew,
        selectedOptions,
        shouldAutocomplete,
        toggleIsListOpen,
        toggleOption,
        value,
    ]);
    const handleKeyUp = (e) => {
        e.preventDefault();
        switch (e.key) {
            case "Enter":
            case "Accept":
                onEnter(e);
                break;
            case "Home":
                toggleIsListOpen(false);
                virtualFocus.moveFocusToTop();
                break;
            case "End":
                toggleIsListOpen(true);
                virtualFocus.moveFocusToBottom();
                break;
            default:
                break;
        }
    };
    const handleKeyDown = useCallback((e) => {
        setIsMouseLastUsedInputDevice(false);
        if (e.key === "Backspace") {
            if (value === "" && shouldShowSelectedOptions) {
                const lastSelectedOption = selectedOptions[selectedOptions.length - 1];
                if (lastSelectedOption) {
                    removeSelectedOption(lastSelectedOption);
                }
            }
        }
        else if (e.key === "Enter" || e.key === "Accept") {
            if (activeDecendantId || value) {
                e.preventDefault();
            }
        }
        else if (e.key === "Escape") {
            if (isListOpen || value) {
                e.preventDefault(); // Prevents closing an encasing Modal, as Combobox reacts on keyup.
                clearInput(e);
                toggleIsListOpen(false);
            }
        }
        else if (["ArrowLeft", "ArrowRight"].includes(e.key)) {
            /**
             * In case user has an active selection and 'completes' the selection with ArrowLeft or ArrowRight
             * we need to make sure to update the filter.
             */
            if (value !== "" && value !== searchTerm) {
                onChange(value);
            }
        }
        else if (e.key === "ArrowDown") {
            // Reset the value to the search term to cancel autocomplete
            // if the user moves focus down to the FilteredOptions
            if (value !== searchTerm) {
                setValue(searchTerm);
            }
            if (virtualFocus.activeElement === null || !isListOpen) {
                toggleIsListOpen(true);
            }
            virtualFocus.moveFocusDown();
        }
        else if (e.key === "ArrowUp") {
            if (value !== "" && value !== searchTerm) {
                onChange(value);
            }
            // Check that the FilteredOptions list is open and has virtual focus.
            // Otherwise ignore keystrokes, so it doesn't interfere with text editing
            if (isListOpen && activeDecendantId) {
                e.preventDefault();
                if (virtualFocus.isFocusOnTheTop()) {
                    toggleIsListOpen(false);
                }
                virtualFocus.moveFocusUp();
            }
        }
    }, [
        value,
        selectedOptions,
        removeSelectedOption,
        isListOpen,
        activeDecendantId,
        setIsMouseLastUsedInputDevice,
        clearInput,
        toggleIsListOpen,
        onChange,
        virtualFocus,
        setValue,
        searchTerm,
        shouldShowSelectedOptions,
    ]);
    const onChangeHandler = useCallback((event) => {
        const newValue = event.target.value;
        if (newValue && newValue !== "") {
            toggleIsListOpen(true);
        }
        else if (filteredOptions.length === 0) {
            toggleIsListOpen(false);
        }
        virtualFocus.moveFocusToTop();
        onChange(newValue);
    }, [filteredOptions.length, virtualFocus, onChange, toggleIsListOpen]);
    return (React.createElement("input", Object.assign({}, rest, omit(inputProps, ["aria-invalid"]), { ref: mergedRefs, value: value, onBlur: () => virtualFocus.moveFocusToTop(), onClick: () => {
            setHideCaret(!!(maxSelected === null || maxSelected === void 0 ? void 0 : maxSelected.isLimitReached));
            value !== searchTerm && onChange(value);
        }, onInput: onChangeHandler, type: "text", role: "combobox", onKeyUp: handleKeyUp, onKeyDown: handleKeyDown, "aria-controls": filteredOptionsUtil.getFilteredOptionsId(inputProps.id), "aria-expanded": !!isListOpen, autoComplete: "off", "aria-autocomplete": shouldAutocomplete ? "both" : "list", "aria-activedescendant": activeDecendantId, "aria-describedby": ariaDescribedBy, "aria-invalid": inputProps["aria-invalid"], placeholder: selectedOptions.length ? undefined : placeholder, className: cl(inputClassName, "navds-combobox__input", "navds-body-short", `navds-body-short--${size}`, { "navds-combobox__input--hide-caret": hideCaret }) })));
});
export default Input;
//# sourceMappingURL=Input.js.map